export const NEWSLETTER_POPUP_DISMISS_TTL_MS = 15 * 24 * 60 * 60 * 1000;
export const NEWSLETTER_POPUP_STATE_STORAGE_KEY =
  "gameplaysync.newsletterPopup.state.v2";
export const NEWSLETTER_POPUP_SESSION_STORAGE_KEY =
  "gameplaysync.newsletterPopup.dismissed.session.v1";

const getBrowserStorage = (storageName) => {
  if (typeof window === "undefined") {
    return null;
  }

  try {
    return window[storageName] || null;
  } catch {
    return null;
  }
};

const readState = (storageKey) => {
  const storage = getBrowserStorage("localStorage");
  if (!storage) {
    return {};
  }

  try {
    const rawState = storage.getItem(storageKey);
    if (!rawState) {
      return {};
    }

    const parsedState = JSON.parse(rawState);
    return parsedState && typeof parsedState === "object" ? parsedState : {};
  } catch {
    return {};
  }
};

const writeState = (storageKey, state) => {
  const storage = getBrowserStorage("localStorage");
  if (!storage) {
    return false;
  }

  try {
    storage.setItem(storageKey, JSON.stringify(state));
    return true;
  } catch {
    return false;
  }
};

const resolveTimestamp = (value) =>
  Number.isFinite(value) ? value : Date.now();

export const isNewsletterPopupAutoSuppressed = ({
  now = Date.now(),
  dismissTtlMs = NEWSLETTER_POPUP_DISMISS_TTL_MS,
  stateStorageKey = NEWSLETTER_POPUP_STATE_STORAGE_KEY,
  sessionStorageKey = NEWSLETTER_POPUP_SESSION_STORAGE_KEY,
} = {}) => {
  const sessionStorage = getBrowserStorage("sessionStorage");

  try {
    if (sessionStorage?.getItem(sessionStorageKey) === "true") {
      return true;
    }
  } catch {
    // Fall through to the durable browser state.
  }

  const state = readState(stateStorageKey);
  if (state.subscribed === true) {
    return true;
  }

  const dismissedAt = Number(state.dismissedAt);
  if (!Number.isFinite(dismissedAt)) {
    return false;
  }

  const elapsedMs = resolveTimestamp(now) - dismissedAt;
  return elapsedMs < Math.max(0, dismissTtlMs);
};

export const markNewsletterPopupDismissed = ({
  now = Date.now(),
  stateStorageKey = NEWSLETTER_POPUP_STATE_STORAGE_KEY,
  sessionStorageKey = NEWSLETTER_POPUP_SESSION_STORAGE_KEY,
} = {}) => {
  const state = readState(stateStorageKey);
  const localStateWritten = writeState(stateStorageKey, {
    ...state,
    dismissedAt: resolveTimestamp(now),
  });
  const sessionStorage = getBrowserStorage("sessionStorage");
  let sessionStateWritten = false;

  try {
    sessionStorage?.setItem(sessionStorageKey, "true");
    sessionStateWritten = Boolean(sessionStorage);
  } catch {
    sessionStateWritten = false;
  }

  return localStateWritten || sessionStateWritten;
};

export const markNewsletterPopupSubscribed = ({
  now = Date.now(),
  stateStorageKey = NEWSLETTER_POPUP_STATE_STORAGE_KEY,
} = {}) => {
  const state = readState(stateStorageKey);

  return writeState(stateStorageKey, {
    ...state,
    subscribed: true,
    subscribedAt: resolveTimestamp(now),
  });
};
